import React, { useState, useEffect, Fragment, useRef, useMemo, useCallback } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon, CalendarIcon, PhotoIcon, ChevronDownIcon, InformationCircleIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import Select from 'react-select';
import { Calendar } from "react-date-range";
import dayjs from "dayjs";
import { toast } from "sonner";
import { getPaymentTypes, getUsersForPaymentReciever, getCounsellorsForPaymentReciever, recordOfflinePayment, getLeadPaymentLinks } from "services/paymentServices";
import { useAuthContext } from "app/contexts/auth/context";

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import 'styles/date-range-picker.css'; // custom styles

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

const parseUtcDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    if (typeof dateStr !== 'string') return new Date(dateStr);
    const normalized = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : `${dateStr}Z`;
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? new Date(dateStr) : parsed;
};

const PaymentDetailsModal = ({ isOpen, onClose, leadId, payment, transactions, pendingAmount, isRefundMode = false }) => {
    const { user: loggedInUser } = useAuthContext();

    const [paymentTypes, setPaymentTypes] = useState([]);
    const [paidToList, setPaidToList] = useState([]);
    const [receivedByList, setReceivedByList] = useState([]);
    const [fetchedTransactions, setFetchedTransactions] = useState([]);
    const [isLoadingPaymentTypes, setIsLoadingPaymentTypes] = useState(false);
    const [isLoadingPaidTo, setIsLoadingPaidTo] = useState(false);
    const [isLoadingReceivedBy, setIsLoadingReceivedBy] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const cancelButtonRef = useRef(null);
    const fileInputRef = useRef(null);
    const hasPatchedReceivedByRef = useRef(false);
    const hasInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const [hasPrefilled, setHasPrefilled] = useState(false);
    const [modalPendingAmount, setModalPendingAmount] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);

    const activePendingAmount = pendingAmount !== undefined ? pendingAmount : modalPendingAmount;

    const [formData, setFormData] = useState({
        source: "",
        paidTo: "",
        receivedBy: "",
        amount: "",
        paymentDate: new Date(),
        attachments: [],
        existingAttachments: [],
        notes: ""
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            fetchInitialData();
            setErrors({});
        } else {
            setFetchedTransactions([]);
            hasInitializedRef.current = false;
            setHasPrefilled(false);
        }
    }, [isOpen]);

    // 1. Initialize form once on modal open or payment object change
    useEffect(() => {
        if (isOpen) {
            if (payment) {
                const primaryPayment = payment.Payments?.[0];
                let initialExisting = [];
                const attachmentsSource = primaryPayment?.Attachments || payment?.Attachments;
                if (Array.isArray(attachmentsSource)) {
                    initialExisting = attachmentsSource;
                } else if (typeof attachmentsSource === 'string') {
                    try {
                        initialExisting = JSON.parse(attachmentsSource) || [];
                    } catch (e) {
                        console.error("Failed to parse attachments:", e);
                    }
                }
                const attachmentPathSource = primaryPayment?.AttachmentPath || payment?.AttachmentPath || payment?.AttachmentUrl || payment?.Attachment;
                const paymentIdSource = primaryPayment?.PaymentId || payment?.PaymentId;
                if (initialExisting.length === 0 && attachmentPathSource) {
                    initialExisting = [{
                        AttachmentId: paymentIdSource || 'legacy',
                        PaymentId: paymentIdSource,
                        FileName: attachmentPathSource.substring(attachmentPathSource.lastIndexOf('/') + 1) || 'Attachment',
                        FilePath: attachmentPathSource
                    }];
                }

                setFormData({
                    source: "", // Set to empty initially, matched once paymentTypes is loaded
                    paidTo: payment.PaidTo || payment.paidTo || primaryPayment?.PaidTo || primaryPayment?.paidTo || "",
                    receivedBy: primaryPayment?.ReceivedBy || primaryPayment?.receivedBy || payment?.ReceivedBy || payment?.receivedBy || "",
                    amount: payment.Amount?.toString() || payment.amount?.toString() || primaryPayment?.PaymentAmount?.toString() || primaryPayment?.paymentAmount?.toString() || payment?.PaymentAmount?.toString() || "",
                    paymentDate: payment.PaymentDate ? parseUtcDate(payment.PaymentDate) : (payment.paymentDate ? parseUtcDate(payment.paymentDate) : (payment.CreatedDate ? parseUtcDate(payment.CreatedDate) : (payment.createdDate ? parseUtcDate(payment.createdDate) : (primaryPayment?.PaymentDate ? parseUtcDate(primaryPayment.PaymentDate) : (primaryPayment?.paymentDate ? parseUtcDate(primaryPayment.paymentDate) : (primaryPayment?.PaymentTime ? parseUtcDate(primaryPayment.PaymentTime) : (primaryPayment?.paymentTime ? parseUtcDate(primaryPayment.paymentTime) : new Date()))))))),
                    attachments: [],
                    existingAttachments: isRefundMode ? [] : initialExisting,
                    notes: isRefundMode ? "" : (primaryPayment?.Notes || primaryPayment?.notes || payment?.Notes || payment?.notes || payment?.PaymentMessage || "")
                });
                hasInitializedRef.current = true;
            } else {
                setFormData({
                    source: "",
                    paidTo: "",
                    receivedBy: "",
                    amount: "",
                    paymentDate: new Date(),
                    attachments: [],
                    existingAttachments: [],
                    notes: ""
                });
                hasInitializedRef.current = true;
            }
        }
    }, [isOpen, payment, isRefundMode]);

    // 2. Resolve and patch source/paymentMethod once paymentTypes are loaded in Edit Mode
    useEffect(() => {
        if (isOpen && payment && paymentTypes.length > 0) {
            const primaryPayment = payment.Payments?.[0];
            const existingPaymentMethod = primaryPayment?.PaymentMethod || payment?.PaymentMethod;
            const matchedType = paymentTypes.find(pt =>
                pt.Code?.toLowerCase() === existingPaymentMethod?.toLowerCase() ||
                pt.PaymentName?.toLowerCase() === existingPaymentMethod?.toLowerCase() ||
                pt.Id?.toString().toLowerCase() === existingPaymentMethod?.toString().toLowerCase()
            );
            if (matchedType) {
                setFormData(prev => {
                    if (!prev.source) {
                        return { ...prev, source: matchedType.Id };
                    }
                    return prev;
                });
            }
        }
    }, [isOpen, payment, paymentTypes]);

    // 3. Prefill paidTo and receivedBy fields from the latest transaction in Add Mode
    useEffect(() => {
        if (isOpen && !payment && !hasPrefilled && !isLoadingPaidTo && !isLoadingReceivedBy && !isFetchingRef.current && paidToList.length > 0 && receivedByList.length > 0) {
            const activeTransactions = (transactions && transactions.length > 0) ? transactions : fetchedTransactions;
            const hasDataToPrefill = Array.isArray(activeTransactions) && activeTransactions.length > 0;
            const isFetchCompleted = (transactions && transactions.length > 0) || (transactions === null && fetchedTransactions.length > 0) || (transactions === null && fetchedTransactions.length === 0 && !isLoadingPaidTo);

            if (hasDataToPrefill) {
                // Sort active transactions descending by CreatedDate to find the latest
                const sortedTransactions = [...activeTransactions].sort((a, b) => {
                    const dateA = a.CreatedDate ? new Date(a.CreatedDate) : new Date(0);
                    const dateB = b.CreatedDate ? new Date(b.CreatedDate) : new Date(0);
                    return dateB - dateA;
                });

                const lastTxn = sortedTransactions.find(t => 
                    t.PaidTo || t.paidTo || t.PaidToName || t.paidToName ||
                    t.Payments?.some(p => p.ReceivedBy || p.receivedBy || p.ReceivedByName || p.receivedByName)
                );

                if (lastTxn) {
                    const rawPaidTo = lastTxn.PaidTo || lastTxn.paidTo || lastTxn.PaidToName || lastTxn.paidToName || "";
                    const primaryPayment = lastTxn.Payments?.find(p => p.ReceivedBy || p.receivedBy || p.ReceivedByName || p.receivedByName) || lastTxn.Payments?.[0];
                    const rawReceivedBy = primaryPayment?.ReceivedBy || primaryPayment?.receivedBy || primaryPayment?.ReceivedByName || primaryPayment?.receivedByName || "";

                    let resolvedPaidTo = "";
                    if (rawPaidTo) {
                        const matchedUser = paidToList.find(u => 
                            u.UserId?.toString().toLowerCase() === rawPaidTo.toString().toLowerCase() ||
                            u.FullName?.toString().toLowerCase() === rawPaidTo.toString().toLowerCase()
                        );
                        if (matchedUser) {
                            resolvedPaidTo = matchedUser.UserId;
                        }
                    }

                    let resolvedReceivedBy = "";
                    if (rawReceivedBy) {
                        const matchedCounsellor = receivedByList.find(u => 
                            u.UserId?.toString().toLowerCase() === rawReceivedBy.toString().toLowerCase() ||
                            u.FullName?.toString().toLowerCase() === rawReceivedBy.toString().toLowerCase()
                        );
                        if (matchedCounsellor) {
                            resolvedReceivedBy = matchedCounsellor.UserId;
                        }
                    }

                    if (resolvedPaidTo || resolvedReceivedBy) {
                        setFormData(prev => ({
                            ...prev,
                            paidTo: prev.paidTo || resolvedPaidTo,
                            receivedBy: prev.receivedBy || resolvedReceivedBy
                        }));
                    }
                }
            }
            
            if (hasDataToPrefill || isFetchCompleted) {
                setHasPrefilled(true);
            }
        }
    }, [isOpen, payment, transactions, fetchedTransactions, isLoadingPaidTo, isLoadingReceivedBy, paidToList, receivedByList, hasPrefilled]);

    // 4. Fallback to logged-in user for receivedBy if no transaction prefilled it
    useEffect(() => {
        if (!isOpen) {
            hasPatchedReceivedByRef.current = false;
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(null);
            return;
        }

        if (isOpen && !payment && hasPrefilled && !hasPatchedReceivedByRef.current && receivedByList.length > 0 && loggedInUser) {
            const currentUserIdStr = (loggedInUser.userId || loggedInUser.id)?.toString().toLowerCase();
            if (currentUserIdStr) {
                const matchedUser = receivedByList.find(u => u.UserId?.toString().toLowerCase() === currentUserIdStr);
                if (matchedUser) {
                    hasPatchedReceivedByRef.current = true;
                    setFormData(prev => {
                        if (!prev.receivedBy) {
                            return { ...prev, receivedBy: matchedUser.UserId };
                        }
                        return prev;
                    });
                }
            }
        }
    }, [isOpen, payment, hasPrefilled, receivedByList, loggedInUser]);

    const fetchInitialData = async () => {
        isFetchingRef.current = true;
        setIsLoadingPaymentTypes(true);
        setIsLoadingPaidTo(true);
        setIsLoadingReceivedBy(true);

        try {
            const promises = [
                getPaymentTypes(),
                getUsersForPaymentReciever(),
                getCounsellorsForPaymentReciever()
            ];

            const shouldFetchTransactions = !payment && leadId && (!transactions || transactions.length === 0);
            if (shouldFetchTransactions) {
                promises.push(getLeadPaymentLinks(leadId));
            }

            const results = await Promise.all(promises);
            const [typesRes, receiversRes, counsellorsRes, linksRes] = results;

            if (typesRes.success) {
                setPaymentTypes(typesRes.data || []);
            }

            if (receiversRes.success) {
                setPaidToList(receiversRes.data || []);
            }

            if (counsellorsRes.success) {
                setReceivedByList(counsellorsRes.data || []);
            }

            if (shouldFetchTransactions && linksRes?.success) {
                setFetchedTransactions(linksRes.data || []);
                if (linksRes.rawResponse?.pendingFees !== undefined) {
                    setModalPendingAmount(linksRes.rawResponse.pendingFees);
                }
            }
        } catch (error) {
            console.error("Error fetching initial payment data:", error);
            toast.error("Failed to load some form data");
        } finally {
            setIsLoadingPaymentTypes(false);
            setIsLoadingPaidTo(false);
            setIsLoadingReceivedBy(false);
            isFetchingRef.current = false;
        }
    };

    const handleInputChange = (e) => {
        let { name, value } = e.target;

        if (name === "amount") {
            // Remove everything except digits (no decimals, no negatives, no alphabets)
            value = value.replace(/\D/g, "");
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleFile = useCallback((file) => {
        if (file) {
            // Validation
            if (!ALLOWED_TYPES.includes(file.type)) {
                toast.error("Only PDF, PNG, and JPG formats are supported");
                return;
            }

            setFormData(prev => ({ ...prev, attachments: [...(prev.attachments || []), file] }));
            setErrors(prev => {
                if (prev.attachment) {
                    const newErrors = { ...prev };
                    delete newErrors.attachment;
                    return newErrors;
                }
                return prev;
            });
        }
    }, []);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            handleFile(file);
        });
        e.target.value = ''; // Reset input to allow selecting same file
    };

    const handlePreviewAttachment = (attOrFile) => {
        let url = "";
        let isPdf = false;
        if (attOrFile instanceof File) {
            url = URL.createObjectURL(attOrFile);
            isPdf = attOrFile.type === "application/pdf";
        } else {
            url = attOrFile.FilePath;
            isPdf = attOrFile.FileType?.toLowerCase() === "application/pdf" || attOrFile.FilePath?.toLowerCase().endsWith(".pdf");
        }

        if (isPdf) {
            window.open(url, "_blank");
        } else {
            setPreviewUrl(url);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSaving) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (isSaving) return;

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFile(file);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.source) newErrors.source = "Source is required";
        if (!formData.amount) newErrors.amount = "Amount is required";
        if (!formData.paidTo) newErrors.paidTo = "Paid To is required";
        if (!formData.receivedBy) newErrors.receivedBy = "Received By is required";

        const selectedType = paymentTypes.find(t => t.Id === formData.source);
        const isUPI = selectedType?.PaymentName?.toLowerCase().includes("upi");

        const totalAttachmentsCount = (formData.attachments?.length || 0) + (formData.existingAttachments?.length || 0);
        if (isUPI && totalAttachmentsCount === 0) {
            newErrors.attachment = "Attachment is mandatory for UPI case";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            toast.error("Please fill all mandatory fields");
            return;
        }

        setIsSaving(true);
        try {
            const selectedType = paymentTypes.find(t => t.Id === formData.source);
            const paymentMethodCode = selectedType?.Code || selectedType?.PaymentName || formData.source;
            const isUPI = selectedType?.PaymentName?.toLowerCase().includes("upi");

            let formattedPaymentDate = undefined;
            if (formData.paymentDate) {
                // Send only the date part (YYYY-MM-DD) since the backend column is date-only.
                // Avoids UTC offset shifting the date (e.g. toISOString() on midnight IST would give the previous day in UTC).
                formattedPaymentDate = dayjs(formData.paymentDate).format('YYYY-MM-DD');
            }

            const result = await recordOfflinePayment({
                LeadId: leadId,
                PaymentMethod: paymentMethodCode,
                Amount: formData.amount,
                PaidTo: formData.paidTo || undefined,
                ReceivedBy: formData.receivedBy || undefined,
                PaymentDate: formattedPaymentDate,
                Notes: formData.notes || undefined,
                Attachments: isUPI ? formData.attachments : [],
                ExistingAttachmentsJson: isUPI ? JSON.stringify(formData.existingAttachments) : undefined,
                PaymentId: payment?.Payments?.[0]?.PaymentId || payment?.PaymentId || undefined,
                VerifyStatus: isRefundMode ? 3 : 0,
            });

            if (result.success) {
                toast.success(result.message || "Payment recorded successfully!");
                // console.log("result", result)
                onClose(result); // Pass the result back

            } else {
                toast.error(result.error || "Failed to record payment");
            }
        } catch (error) {
            console.error("Error recording offline payment:", error);
            toast.error("An error occurred while saving payment details");
        } finally {
            setIsSaving(false);
        }
    };

    const paymentMethodOptions = useMemo(() => {
        const excludedSources = ["card transfer", "bank transfer", "cheque"];
        return paymentTypes
            .filter(pt => pt.PaymentName && !excludedSources.includes(pt.PaymentName.toLowerCase()))
            .map(pt => ({
                value: pt.Id,
                label: pt.PaymentName
            }));
    }, [paymentTypes]);

    const paidToOptions = useMemo(() => {
        return paidToList.map(user => ({
            value: user.UserId,
            label: user.FullName
        }));
    }, [paidToList]);

    const receivedByOptions = useMemo(() => {
        return receivedByList.map(user => ({
            value: user.UserId,
            label: user.FullName
        }));
    }, [receivedByList]);

    const selectedType = paymentTypes.find(t => t.Id === formData.source);
    const isUPI = selectedType?.PaymentName?.toLowerCase().includes("upi");

    useEffect(() => {
        const handlePaste = (e) => {
            if (!isOpen || isSaving || !isUPI) return;

            const files = e.clipboardData?.files;
            if (files && files.length > 0) {
                const file = files[0];
                if (file) {
                    let pastedFile = file;
                    if (file.name === "image.png" || file.name === "blob") {
                        const extension = file.type.split("/")[1] || "png";
                        pastedFile = new File([file], `screenshot_${dayjs().format('YYYYMMDD_HHmmss')}.${extension}`, { type: file.type });
                    }
                    handleFile(pastedFile);
                    e.preventDefault();
                }
            }
        };

        if (isOpen) {
            window.addEventListener("paste", handlePaste);
        }

        return () => {
            window.removeEventListener("paste", handlePaste);
        };
    }, [isOpen, isSaving, isUPI, handleFile]);

    const currencySymbol = '₹';

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            color: '#1f2937', // Dark gray text for readability
            backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
            cursor: 'pointer'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1f2937', // Dark gray text for the selected item
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999, // Ensures dropdown doesn't hide behind other elements
        })
    };

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[99998] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
                    onClose={() => { }}
                    initialFocus={cancelButtonRef}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm dark:bg-black/40" />
                    </TransitionChild>

                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-300 dark:bg-dark-700 outline-none">
                            {/* Header */}
                            <div className="border-b border-gray-100 dark:border-dark-600 px-6 py-4 flex items-center justify-between bg-white dark:bg-dark-700">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                        {isRefundMode ? "Refund Payment" : payment ? "Edit Payment Details" : "Payment Details"}
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                                        {isRefundMode ? "Process refund for this payment" : payment ? "Update the payment information" : "Record the payment information manually"}
                                    </p>
                                </div>
                                <Button
                                    onClick={onClose}
                                    variant="flat"
                                    isIcon
                                    className="size-9 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-gray-200 transition-colors"
                                >
                                    <XMarkIcon className="size-5" />
                                </Button>
                            </div>

                            {/* Content */}
                            <div className="flex flex-col overflow-y-auto px-6 py-6 space-y-5 max-h-[70vh]">
                                <div className="space-y-4">
                                    {/* Source Selector */}
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Source <span className="text-red-500">*</span>
                                        </label>
                                        {isLoadingPaymentTypes ? (
                                            <div className="flex gap-2.5 animate-pulse">
                                                <div className="h-10 w-24 bg-gray-200 dark:bg-dark-600 rounded-lg"></div>
                                                <div className="h-10 w-24 bg-gray-200 dark:bg-dark-600 rounded-lg"></div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-wrap gap-3">
                                                {paymentMethodOptions.map((opt) => {
                                                    const isSelected = opt.value?.toString().toLowerCase() === formData.source?.toString().toLowerCase();
                                                    const isCash = opt.label?.toLowerCase().includes("cash");
                                                    const isUPI = opt.label?.toLowerCase().includes("upi");
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            disabled={isSaving}
                                                            onClick={() => handleSelectChange('source', opt.value)}
                                                            className={`flex-1 sm:flex-none sm:w-[140px] h-10 flex items-center gap-2 px-3 rounded-lg border transition-all duration-200 cursor-pointer select-none text-left ${isSelected
                                                                    ? "border-blue-500 bg-blue-50/20 text-blue-600 dark:bg-blue-900/10 dark:text-blue-400 dark:border-blue-500"
                                                                    : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700 dark:border-gray-750 dark:bg-dark-750 dark:text-gray-200 hover:border-gray-300"
                                                                }`}
                                                        >
                                                            {/* Radio Circle */}
                                                            <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${isSelected ? "border-blue-500" : "border-gray-300 dark:border-gray-600"
                                                                }`}>
                                                                {isSelected && (
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400" />
                                                                )}
                                                            </div>

                                                            {/* Label */}
                                                            <span className="text-xs font-bold">
                                                                {opt.label}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {errors.source && <p className="text-xs text-red-500 mt-1">{errors.source}</p>}
                                    </div>

                                    {/* Paid To & Received By side by side */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Paid To Selector */}
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Paid To <span className="text-red-500">*</span>
                                            </label>
                                            <Select
                                                options={paidToOptions}
                                                value={paidToOptions.find(opt => opt.value?.toString().toLowerCase() === formData.paidTo?.toString().toLowerCase())}
                                                onChange={(opt) => handleSelectChange('paidTo', opt?.value || '')}
                                                isLoading={isLoadingPaidTo}
                                                placeholder="Select paid to..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                isDisabled={isSaving || isRefundMode}
                                                isClearable
                                                isSearchable={false}
                                                styles={customSelectStyles}
                                            />
                                            {errors.paidTo && <p className="text-xs text-red-500 mt-1">{errors.paidTo}</p>}
                                        </div>

                                        {/* Received By Selector */}
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Received By <span className="text-red-500">*</span>
                                            </label>
                                            <Select
                                                options={receivedByOptions}
                                                value={receivedByOptions.find(opt => opt.value?.toString().toLowerCase() === formData.receivedBy?.toString().toLowerCase())}
                                                onChange={(opt) => handleSelectChange('receivedBy', opt?.value || '')}
                                                isLoading={isLoadingReceivedBy}
                                                placeholder="Select received by..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                isDisabled={isSaving || isRefundMode}
                                                isClearable
                                                isSearchable={false}
                                                styles={customSelectStyles}
                                            />
                                            {errors.receivedBy && <p className="text-xs text-red-500 mt-1">{errors.receivedBy}</p>}
                                        </div>
                                    </div>
                                    {/* Quick Amounts Selector Section */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Quick Amounts
                                            </label>
                                            {activePendingAmount > 0 && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded border border-red-200 bg-red-50 text-[11px] font-bold text-red-600 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-400 shadow-sm transition-all">
                                                    Pending: {currencySymbol}{activePendingAmount.toLocaleString('en-IN')}
                                                </span>
                                            )}
                                        </div>
                                        <div className={`grid gap-1.5 ${activePendingAmount > 0 ? "grid-cols-5" : "grid-cols-4"}`}>
                                            {(() => {
                                                const quickValues = [1000, 1500, 2000, 2500];
                                                if (activePendingAmount > 0) {
                                                    quickValues.push(activePendingAmount);
                                                }
                                                return quickValues.map((val, idx) => {
                                                    const isSelected = formData.amount === val.toString();
                                                    const isPendingVal = idx === 4 && activePendingAmount > 0;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={() => setFormData(prev => ({ ...prev, amount: val.toString() }))}
                                                            className={`h-8 flex items-center justify-center text-[11px] font-semibold rounded-lg border transition-all duration-150 cursor-pointer text-center px-1 truncate ${
                                                                isSelected
                                                                    ? "bg-blue-50/50 border-blue-500 text-blue-600 dark:bg-blue-900/10 dark:border-blue-500 dark:text-blue-400 font-bold shadow-sm"
                                                                    : "bg-white hover:bg-gray-50 border-gray-200 dark:bg-dark-750 dark:hover:bg-dark-600 dark:border-dark-600 text-gray-700 dark:text-dark-200 hover:text-blue-600 hover:border-blue-400"
                                                            }`}
                                                            title={isPendingVal ? `Full Pending Amount: ${currencySymbol}${val.toLocaleString('en-IN')}` : `${currencySymbol}${val.toLocaleString('en-IN')}`}
                                                        >
                                                            {currencySymbol}{val.toLocaleString('en-IN')}
                                                        </button>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>

                                    {/* Amount & Payment Date inputs side by side */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Amount Input */}
                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-350">
                                                Amount <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-sm font-bold text-gray-500 dark:text-dark-400">{currencySymbol}</span>
                                                </div>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    name="amount"
                                                    value={formData.amount}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pl-7 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                                    disabled={isSaving}
                                                    required
                                                />
                                                {formData.amount && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({ ...prev, amount: "" }))}
                                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200"
                                                    >
                                                        <XMarkIcon className="size-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Payment Date Input */}
                                        <div className="relative">
                                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Payment Date <span className="text-red-500">*</span>
                                            </label>
                                            <Popover className="relative">
                                                {({ open }) => (
                                                    <>
                                                        <PopoverButton
                                                            disabled={isSaving}
                                                            className="w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <div className="flex items-center min-w-0">
                                                                <CalendarIcon className="size-5 text-gray-400 dark:text-dark-400 mr-2 shrink-0" />
                                                                <span className={formData.paymentDate ? "text-gray-900 dark:text-gray-100 truncate" : "text-gray-500 dark:text-gray-400 truncate"}>
                                                                    {formData.paymentDate
                                                                        ? dayjs(formData.paymentDate).format('MMMM D, YYYY')
                                                                        : "Select payment date"}
                                                                </span>
                                                            </div>
                                                            <ChevronDownIcon className="size-4 text-gray-400 dark:text-dark-400 shrink-0 ml-2" />
                                                        </PopoverButton>

                                                        <Transition
                                                            as={Fragment}
                                                            enter="transition ease-out duration-100"
                                                            enterFrom="transform opacity-0 scale-95"
                                                            enterTo="transform opacity-100 scale-100"
                                                            leave="transition ease-in duration-75"
                                                            leaveFrom="transform opacity-100 scale-100"
                                                            leaveTo="transform opacity-0 scale-95"
                                                        >
                                                            <PopoverPanel
                                                                ref={(el) => {
                                                                    if (el) {
                                                                        setTimeout(() => {
                                                                            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                        }, 100);
                                                                    }
                                                                }}
                                                                className="absolute z-[99999] mt-2 right-0 bg-white dark:bg-[#1f2937] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
                                                            >
                                                                <Calendar
                                                                    date={formData.paymentDate || new Date()}
                                                                    onChange={(date) => {
                                                                        setFormData(prev => ({ ...prev, paymentDate: date }));
                                                                        if (errors.paymentDate) {
                                                                            setErrors(prev => {
                                                                                const newErrors = { ...prev };
                                                                                delete newErrors.paymentDate;
                                                                                return newErrors;
                                                                            });
                                                                        }
                                                                    }}
                                                                    maxDate={new Date()}
                                                                    color="#3b82f6"
                                                                    showDateDisplay={false}
                                                                />
                                                            </PopoverPanel>
                                                        </Transition>
                                                    </>
                                                )}
                                            </Popover>
                                            {errors.paymentDate && <p className="text-xs text-red-500 mt-1">{errors.paymentDate}</p>}
                                        </div>
                                    </div>

                                    {/* Attachment (UPI case) */}
                                    {isUPI && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Attachment <span className="text-red-500">*</span>
                                            </label>
                                            <div
                                                onClick={() => !isSaving && fileInputRef.current?.click()}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                                className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-all duration-200 ${((formData.attachments?.length || 0) + (formData.existingAttachments?.length || 0)) > 0
                                                    ? "bg-primary-50/20 border-primary-200 dark:bg-primary-900/5 dark:border-primary-800"
                                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100 dark:bg-dark-750 dark:border-dark-600 dark:hover:bg-dark-600"
                                                    } ${isDragging ? "border-primary-500 bg-primary-100/50 dark:bg-primary-900/20 scale-[1.02] shadow-sm" : ""} ${errors.attachment ? "border-red-500 bg-red-50/50" : ""} ${isSaving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    className="hidden"
                                                    onChange={handleFileChange}
                                                    accept=".pdf,.png,.jpg,.jpeg"
                                                    multiple
                                                    disabled={isSaving}
                                                />
                                                <div className="flex flex-col items-center">
                                                    <CloudArrowUpIcon className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 text-center">
                                                        {((formData.attachments?.length || 0) + (formData.existingAttachments?.length || 0)) > 0
                                                            ? "Click, drag, or paste to add more attachments"
                                                            : "Click, drag, or Ctrl+V to paste screenshot"}
                                                    </span>
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                        PDF, PNG, JPG
                                                    </span>
                                                </div>
                                            </div>
                                            {errors.attachment && <p className="text-xs text-red-500 mt-1">{errors.attachment}</p>}

                                            {/* List of files */}
                                            {((formData.attachments?.length || 0) + (formData.existingAttachments?.length || 0)) > 0 && (
                                                <div className="mt-3 space-y-2 max-h-40 overflow-y-auto pr-1">
                                                    {/* Existing files */}
                                                    {formData.existingAttachments?.map((att, idx) => (
                                                        <div key={att.AttachmentId || idx} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-150 dark:border-gray-700">
                                                            <div
                                                                onClick={() => handlePreviewAttachment(att)}
                                                                className="flex items-center gap-2 min-w-0 cursor-pointer group flex-1"
                                                                title="Click to preview"
                                                            >
                                                                <DocumentIcon className="w-5 h-5 text-gray-500 shrink-0 group-hover:text-primary-500 transition-colors" />
                                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 group-hover:underline transition-colors" title={att.FileName}>
                                                                    {att.FileName}
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        existingAttachments: prev.existingAttachments.filter((_, i) => i !== idx)
                                                                    }));
                                                                }}
                                                                className="text-xs text-red-500 hover:text-red-700 font-semibold ml-2 shrink-0"
                                                                disabled={isSaving}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}

                                                    {/* New files */}
                                                    {formData.attachments?.map((file, idx) => (
                                                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-primary-50/50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/30">
                                                            <div
                                                                onClick={() => handlePreviewAttachment(file)}
                                                                className="flex items-center gap-2 min-w-0 cursor-pointer group flex-1"
                                                                title="Click to preview"
                                                            >
                                                                <DocumentIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0 group-hover:text-primary-500 transition-colors" />
                                                                <span className="text-xs font-medium text-primary-700 dark:text-primary-300 truncate group-hover:text-primary-800 dark:group-hover:text-primary-200 group-hover:underline transition-colors" title={file.name}>
                                                                    {file.name}
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        attachments: prev.attachments.filter((_, i) => i !== idx)
                                                                    }));
                                                                }}
                                                                className="text-xs text-red-500 hover:text-red-700 font-semibold ml-2 shrink-0"
                                                                disabled={isSaving}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Notes */}
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Notes (Optional)
                                        </label>
                                        <textarea
                                            name="notes"
                                            rows={3}
                                            placeholder="Add any additional notes here..."
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 resize-none min-h-[80px]"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-750">
                                {formData.amount && Number(formData.amount) > 0 ? (
                                    <div className="w-full sm:w-auto">
                                        <div className="flex items-center justify-between sm:justify-start gap-2">
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Amount to be Paid:</span>
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-400 shadow-sm">
                                                {currencySymbol}{Number(formData.amount).toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="hidden sm:block" />
                                )}
                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                    <Button
                                        variant="outlined"
                                        onClick={onClose}
                                        disabled={isSaving}
                                        className="flex-1 sm:flex-none sm:min-w-[100px] justify-center text-center"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="filled"
                                        color={isRefundMode ? "warning" : "primary"}
                                        onClick={handleSubmit}
                                        disabled={isSaving || isLoadingPaymentTypes || isLoadingPaidTo || isLoadingReceivedBy}
                                        className="flex-1 sm:flex-none sm:min-w-[140px] justify-center text-center"
                                    >
                                        {isSaving ? "Saving..." : isRefundMode ? "Refund Payment" : payment ? "Update Payment" : "Save Payment"}
                                    </Button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>

            {/* Image Preview Lightbox Modal */}
            <Transition appear show={!!previewUrl} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[99999]"
                    onClose={() => {
                        if (previewUrl && previewUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(previewUrl);
                        }
                        setPreviewUrl(null);
                    }}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
                    </TransitionChild>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <DialogPanel className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl shadow-2xl transform transition-all">
                                    {/* Close button */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (previewUrl && previewUrl.startsWith('blob:')) {
                                                URL.revokeObjectURL(previewUrl);
                                            }
                                            setPreviewUrl(null);
                                        }}
                                        className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white ring-2 ring-white/40 transition-all hover:bg-black/90 hover:ring-white/70 hover:scale-110"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>

                                    {/* Header label */}
                                    <div className="absolute left-0 top-0 z-10 flex items-center gap-1.5 rounded-br-lg bg-black/50 px-3 py-1.5 text-[11px] font-medium text-white">
                                        <PhotoIcon className="h-3.5 w-3.5" />
                                        UPI Payment Screenshot
                                    </div>

                                    <img
                                        src={previewUrl}
                                        alt="Payment Attachment"
                                        className="block max-h-[90vh] max-w-[90vw] rounded-xl object-contain"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            toast.error('Failed to load attachment');
                                            if (previewUrl && previewUrl.startsWith('blob:')) {
                                                URL.revokeObjectURL(previewUrl);
                                            }
                                            setPreviewUrl(null);
                                        }}
                                    />
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

export default PaymentDetailsModal;
